import axios from 'axios';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Container, Typography, Grid, Chip, InputAdornment, TextField } from '@mui/material';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { ProductList } from '../sections/@dashboard/products';

// ----------------------------------------------------------------------

export default function DestinasiPage() {
  const API_URL = process.env.REACT_APP_API;
  const [kecamatan, setKecamatan] = useState([]);
  const [filter, setFilter] = useState('');

  const getAllKecamatan = async () => {
    try {
      await axios.get(`${API_URL}kecamatan/allKecamatan`)
        .then(({ data }) => { setKecamatan(data?.data); })
        .catch((err) => { if (err.response.status === 404) { setKecamatan([]); } });
    } catch (error) { console.log(error); }
  };

  useEffect(() => {
    getAllKecamatan();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredKecamatan = kecamatan.filter((k) =>
    k.kecamatan?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <>
      <Helmet>
        <title>{localStorage.getItem("judul") !== null ? localStorage.getItem("judul") : ''} | Destinasi</title>
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={window.location.href} />
        <meta name="description" content="Jelajahi destinasi kuliner di seluruh kecamatan Bandung Barat" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no" />
      </Helmet>

      {/* Hero Banner */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1A1A2E 0%, #16213E 50%, #2C1810 100%)',
        pt: { xs: '90px', md: '110px' },
        pb: 6,
        position: 'relative',
        overflow: 'hidden',
      }}>
        {/* Decorative orbs */}
        <Box sx={{
          position: 'absolute', bottom: '-10%', left: '-5%', width: 350, height: 350,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(184,92,44,0.12) 0%, transparent 70%)',
          filter: 'blur(60px)', pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', top: '5%', right: '10%', width: 250, height: 250,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(245,166,35,0.10) 0%, transparent 70%)',
          filter: 'blur(50px)', pointerEvents: 'none',
        }} />

        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 5 } }}>
          <Box sx={{ maxWidth: 700 }}>
            <Chip label="🗺️ Peta Gastronomi" sx={{
              background: 'rgba(245,166,35,0.15)',
              color: '#F5A623',
              fontWeight: 700,
              fontSize: '12px',
              border: '1px solid rgba(245,166,35,0.3)',
              mb: 2.5,
              px: 1,
            }} />
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
              <Box sx={{
                width: 52, height: 52, borderRadius: '16px', flexShrink: 0,
                background: 'linear-gradient(135deg, #B85C2C, #F5A623)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                mt: 0.5,
              }}>
                <ExploreOutlinedIcon sx={{ color: '#fff', fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h2" sx={{
                  color: '#fff',
                  fontWeight: 800,
                  fontFamily: "'Plus Jakarta Sans', sans-serif",
                  fontSize: { xs: '28px', md: '42px' },
                  lineHeight: 1.15,
                  letterSpacing: '-1px',
                  mb: 1,
                }}>
                  Destinasi Kuliner
                </Typography>
                <Typography sx={{
                  color: 'rgba(255,255,255,0.55)',
                  fontSize: { xs: '14px', md: '16px' },
                  lineHeight: 1.6,
                  maxWidth: 520,
                }}>
                  Temukan kekayaan kuliner dari setiap kecamatan di Bandung Barat. Mulai petualangan gastronomi kamu dari sini.
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Search */}
          <Box sx={{ mt: 4, maxWidth: 500 }}>
            <TextField
              fullWidth
              placeholder="Cari kecamatan..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />
                  </InputAdornment>
                ),
                sx: {
                  borderRadius: '50px',
                  background: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  color: '#fff',
                  '& input': { color: '#fff', '&::placeholder': { color: 'rgba(255,255,255,0.45)' } },
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.15)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3) !important' },
                  '&.Mui-focused fieldset': { borderColor: 'rgba(245,166,35,0.5) !important' },
                  px: 1,
                },
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Content */}
      <Container maxWidth="xl" sx={{ py: 6, px: { xs: 2, sm: 3, md: 5 } }}>
        {/* Count */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
          <Box>
            <Typography sx={{
              fontWeight: 800, fontSize: { xs: '20px', md: '26px' },
              color: '#1A1A2E',
              fontFamily: "'Plus Jakarta Sans', sans-serif",
              letterSpacing: '-0.5px',
            }}>
              Semua Kecamatan
            </Typography>
            <Typography sx={{ color: '#6B7280', fontSize: '14px', mt: 0.5 }}>
              {filteredKecamatan.length} kecamatan ditemukan
            </Typography>
          </Box>
          {filter && (
            <Chip
              label={`Filter: "${filter}"`}
              onDelete={() => setFilter('')}
              sx={{ background: 'rgba(184,92,44,0.1)', color: '#B85C2C', fontWeight: 600 }}
            />
          )}
        </Box>

        {/* Grid */}
        <Grid container spacing={3}>
          {filteredKecamatan.map((kec) => (
            <Grid key={kec.id} item xs={6} sm={4} md={3} lg={2}>
              {/* ProductList renders ShopProductCard */}
            </Grid>
          ))}
        </Grid>

        <ProductList kecamatan={filteredKecamatan} />

        {filteredKecamatan.length === 0 && (
          <Box sx={{
            textAlign: 'center', py: 10,
            background: '#fff8f0',
            borderRadius: '24px',
            border: '1px solid rgba(184,92,44,0.1)',
            mt: 2,
          }}>
            <ExploreOutlinedIcon sx={{ fontSize: 56, color: '#B85C2C', opacity: 0.3, mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A1A2E', mb: 1 }}>
              Kecamatan tidak ditemukan
            </Typography>
            <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>
              Coba kata kunci pencarian lain
            </Typography>
          </Box>
        )}
      </Container>
    </>
  );
}
